import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";

import updateSourceDataById from "@app/services/cases/updateSourceDataById";

import { useToast } from "@hooks/useToast";

import { CASES_DATA_STATUS_TYPE, DATA_SOURCE_TYPE } from "@utils/enum";

import {
  fetchAttachments,
  fetchSourceData,
} from "./CrimesTabAlternative.utils";

export const useCrimesDialog = () => {
  const history = useHistory();
  const { messages } = useIntl();
  const { case_id, data_source_id } = useParams();
  const { showSuccessToast, showErrorToast } = useToast();

  const [shouldShowAddCrimeDialog, setShouldShowAddCrimeDialog] =
    useState(false);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [data, setData] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [selectedEditData, setSelectedEditData] = useState(null);
  const [selectedDeleteData, setSelectedDeleteData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");

  const isCrimesEditable =
    !isLoading && status !== CASES_DATA_STATUS_TYPE.COMPLETED;

  const shouldShowRawDataViewer =
    sourceData?.type ===
    DATA_SOURCE_TYPE.VERIFERA_PERSON_INFORMATION_ROLE_ANALYSIS;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const [sourceDataFromResponse, attachmentsData] = await Promise.all([
          fetchSourceData(data_source_id),
          fetchAttachments(data_source_id),
        ]);

        const { data: { crimes = [] } = {}, status: sourceDataStatus } =
          sourceDataFromResponse;

        setData(crimes);
        setSourceData(sourceDataFromResponse);
        setStatus(sourceDataStatus);
        setAttachments(attachmentsData);
      } catch {
        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [data_source_id, messages.exception_error_message, showErrorToast]);

  const handleOpenEditCrimeDialog = value => {
    const crimeDetails = {
      crime: {
        name: value.crime_name,
        id: value.crime_id,
        type: value.crime_type,
      },
      crime_case_number: value.crime_case_number,
      crime_verdict_date: value.crime_verdict_date,
      court: {
        court_name: value.court_name ?? "",
        id: value.court_id,
      },
      crime_sentence: value.crime_sentence,
    };

    setSelectedEditData(crimeDetails);
  };

  const handleOpenDeleteCrimeDialog = value => {
    setSelectedDeleteData(value);
  };

  const config = {
    header: {},
    no_records_message: "datatable_admin_data_source_crimes_no_objects_found",
    columns: [
      {
        db_field: "crime_id",
        type: "text",
        isHidden: true,
      },
      {
        db_field: "court_id",
        type: "text",
        isHidden: true,
      },
      {
        db_field: "crime_name",
        title: "title_crime",
        type: "text",
        sortable: true,
        width: "25%",
      },
      {
        db_field: "crime_case_number",
        title: "title_case_number",
        type: "id",
        sortable: true,
        width: "15%",
      },
      {
        db_field: "crime_verdict_date",
        title: "label_crime_verdict_date",
        type: "date",
        sortable: true,
        width: "20%",
      },
      {
        db_field: "court_name",
        title: "label_court_name",
        type: "text",
        sortable: true,
        width: "20%",
      },
      {
        db_field: "crime_sentence",
        title: "message_penalty",
        type: "text",
        sortable: true,
        width: "15%",
      },
      {
        type: "actions",
        width: "5%",
        isHidden: !isCrimesEditable,
        actions: [
          {
            label: messages.label_edit_crime,
            icon: "icon-pen",
            type: "button",
            onClick: handleOpenEditCrimeDialog,
          },
          {
            label: messages.label_delete_crime,
            icon: "icon-rubber",
            type: "button",
            onClick: handleOpenDeleteCrimeDialog,
          },
        ],
      },
    ],
    static_data: data,
    backend_querying: false,
  };

  const handleSave = () => {
    handleUpdateSourceData();
  };

  const handleUpdateSourceData = async (shouldComplete = false) => {
    try {
      setIsLoading(true);

      const mappedData = data.map(
        ({
          crime_id,
          crime_verdict_date,
          court_id,
          court_name,
          crime_type,
          crime_case_number,
          crime_sentence,
        }) => ({
          crime_id,
          crime_case_number,
          crime_verdict_date,
          court_id,
          court_name,
          crime_type,
          crime_sentence,
          crime_date: crime_verdict_date,
        })
      );
      const payload = { crimes: mappedData };

      if (shouldComplete) {
        payload.status = CASES_DATA_STATUS_TYPE.COMPLETED;
      }

      await updateSourceDataById(case_id, data_source_id, payload);
      showSuccessToast(messages.success_message);

      setIsDataChanged(false);
    } catch {
      showErrorToast(messages.exception_error_message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteCrimesData = async () => {
    await handleUpdateSourceData(true);
    history.goBack();
  };

  const handleOpenAddEditCrimeDialog = () => {
    setShouldShowAddCrimeDialog(true);
  };

  const formatCrimeValues = values => {
    const {
      crime: { id: crime_id, name: crime_name, type: crime_type } = {},
      court: { id: court_id, court_name } = {},
      crime_case_number,
      crime_verdict_date,
      crime_sentence,
    } = values;

    return {
      court_id,
      court_name,
      crime_id,
      crime_name,
      crime_type,
      crime_case_number,
      crime_verdict_date,
      crime_sentence,
    };
  };

  const handleAddCrime = values => {
    const newCrime = formatCrimeValues(values);

    setData(prev => {
      const nextData = prev;
      nextData.push(newCrime);

      return nextData;
    });
    setIsDataChanged(true);

    handleCloseAddCrimeDialog();
  };

  const handleCloseAddCrimeDialog = () => {
    setShouldShowAddCrimeDialog(false);
  };

  const handleEditCrime = values => {
    const updatedCrime = formatCrimeValues(values);

    setData(prev => {
      const nextData = prev;
      const index = prev.findIndex(
        item =>
          item.crime_id === updatedCrime.crime_id &&
          item.court_id === updatedCrime.court_id
      );
      nextData.splice(index, 1, updatedCrime);

      return nextData;
    });
    setIsDataChanged(true);

    handleCloseEditCrimeDialog();
  };

  const handleCloseEditCrimeDialog = () => {
    setSelectedEditData(null);
  };

  const handleDeleteCrime = () => {
    setData(prev => {
      const nextData = prev;
      const deletedElementIndex = data.findIndex(
        ({ crime_id, court_id }) =>
          crime_id === selectedDeleteData.crime_id &&
          court_id === selectedDeleteData.court_id
      );
      nextData.splice(deletedElementIndex, 1);

      return nextData;
    });
    setIsDataChanged(true);

    handleCloseDeleteCrimeDialog();
  };

  const handleCloseDeleteCrimeDialog = () => {
    setSelectedDeleteData(null);
  };

  const tabHeader = {
    subtitle: messages.text_add_edit_crimes,
    actions: [
      {
        id: "new-crime",
        type: "button",
        label: messages.label_add_crime,
        width: "auto",
        icon: "plus",
        variant: "header",
        isHidden: !isCrimesEditable,
        onClick: handleOpenAddEditCrimeDialog,
      },
    ],
  };

  return {
    config,
    shouldShowAddCrimeDialog,
    isCrimesEditable,
    selectedDeleteData,
    selectedEditData,
    shouldShowRawDataViewer,
    sourceData,
    attachments,
    isLoading,
    status,
    tabHeader,
    isDataChanged,
    handleSave,
    handleCompleteCrimesData,
    handleOpenAddEditCrimeDialog,
    handleAddCrime,
    handleCloseAddCrimeDialog,
    handleEditCrime,
    handleCloseEditCrimeDialog,
    handleDeleteCrime,
    handleCloseDeleteCrimeDialog,
  };
};
